/**
 * Elite AI Response Engine
 * Analyzes question type and generates contextual responses
 */

export type QuestionType = 
  | 'priority_focus'      // "What should I focus on today?"
  | 'risk_analysis'        // "Which deal is most at risk?"
  | 'revenue_optimization' // "How can I close €10k faster?"
  | 'pipeline_health'     // "What's the health of my pipeline?"
  | 'action_plan'         // "What should I do?"
  | 'forecast'            // "What's my forecast?"
  | 'stuck_deals'         // "What deals are stuck?"
  | 'next_steps'          // "What are my next steps?"
  | 'general';            // General questions

export interface ContextualResponse {
  daily_brief: string;
  reasoning?: string;
  tone: 'directive' | 'analytical' | 'strategic' | 'actionable';
  focus_areas: string[];
}

/**
 * Detects question type from user prompt
 */
export function detectQuestionType(prompt: string): QuestionType {
  const lower = prompt.toLowerCase();
  
  // Priority & Focus
  if (lower.match(/(what should|what to|focus|priority|prioritize|today|now)/)) {
    return 'priority_focus';
  }
  
  // Risk Analysis
  if (lower.match(/(risk|at risk|exposed|danger|lose|stuck|stalled|overdue)/)) {
    return 'risk_analysis';
  }
  
  // Revenue Optimization
  if (lower.match(/(close|revenue|money|€|euro|dollar|\$|faster|quicker|speed|optimize)/)) {
    return 'revenue_optimization';
  }
  
  // Pipeline Health
  if (lower.match(/(health|status|state|how.*pipeline|pipeline.*look)/)) {
    return 'pipeline_health';
  }
  
  // Forecast
  if (lower.match(/(forecast|projection|expect|likely|probably|chance)/)) {
    return 'forecast';
  }
  
  // Stuck Deals
  if (lower.match(/(stuck|stalled|no.*movement|not.*progress|stuck.*deal)/)) {
    return 'stuck_deals';
  }
  
  // Next Steps
  if (lower.match(/(next|step|action|do.*next|then what)/)) {
    return 'next_steps';
  }
  
  // Action Plan
  if (lower.match(/(plan|strategy|approach|how.*do|what.*do)/)) {
    return 'action_plan';
  }
  
  return 'general';
}

/**
 * Generates contextual response based on question type and data
 */
export function generateContextualResponse(
  questionType: QuestionType,
  data: {
    leads: any[];
    deals: any[];
    metrics: {
      pipelineValue: number;
      needAction: number;
      overdueFollowups: number;
      stuckDeals: number;
      winRate: number;
      forecast: number;
    };
    priorityActions: any[];
  }
): ContextualResponse {
  const { leads, deals, metrics, priorityActions } = data;
  
  switch (questionType) {
    case 'priority_focus':
      return generatePriorityFocusResponse(priorityActions, metrics);
    
    case 'risk_analysis':
      return generateRiskAnalysisResponse(leads, deals, metrics);
    
    case 'revenue_optimization':
      return generateRevenueOptimizationResponse(priorityActions, deals, metrics);
    
    case 'pipeline_health':
      return generatePipelineHealthResponse(leads, deals, metrics);
    
    case 'forecast':
      return generateForecastResponse(deals, metrics);
    
    case 'stuck_deals':
      return generateStuckDealsResponse(deals, metrics);
    
    case 'next_steps':
      return generateNextStepsResponse(priorityActions);
    
    case 'action_plan':
      return generateActionPlanResponse(priorityActions, metrics);
    
    default:
      return generateGeneralResponse(priorityActions, metrics);
  }
}

function generatePriorityFocusResponse(priorityActions: any[], metrics: any): ContextualResponse {
  // V4: Decision > Opinion - Data-driven, not chatty
  const topAction = priorityActions[0];
  
  if (!topAction) {
    return {
      daily_brief: `You have ${metrics.needAction} leads requiring attention. Start by reviewing your pipeline to identify the highest-value opportunities.`,
      tone: 'directive',
      focus_areas: ['Review pipeline', 'Identify high-value leads', 'Plan outreach']
    };
  }
  
  return {
    daily_brief: `Contact ${topAction.lead_name} first. ${topAction.why}. Revenue impact: €${(topAction.revenue_impact || 0).toLocaleString()}. This is your #1 priority because it has the highest combined score of revenue potential, recency, and buying intent.`,
    reasoning: `Analyzed ${priorityActions.length} priority actions. ${topAction.lead_name} scored highest due to ${topAction.why.toLowerCase()}.`,
    tone: 'directive',
    focus_areas: [
      `Immediate: Contact ${topAction.lead_name}`,
      `Today: Complete ${priorityActions.slice(0, 3).map(a => a.lead_name).join(', ')}`,
      `This week: Address ${metrics.needAction} total leads`
    ]
  };
}

function generateRiskAnalysisResponse(leads: any[], deals: any[], metrics: any): ContextualResponse {
  const atRiskDeals = deals.filter(d => {
    const daysSinceUpdate = d.updated_at ? 
      Math.floor((Date.now() - new Date(d.updated_at).getTime()) / (1000 * 60 * 60 * 24)) : 999;
    return daysSinceUpdate > 7 && d.stage !== 'won' && d.stage !== 'lost';
  });
  
  const totalRisk = atRiskDeals.reduce((sum, d) => sum + (d.value || 0), 0);
  
  if (atRiskDeals.length === 0) {
    return {
      daily_brief: `Good news: No deals are currently at high risk. Your pipeline is active with ${deals.length} deals in motion. Continue maintaining momentum.`,
      tone: 'analytical',
      focus_areas: ['Maintain momentum', 'Continue outreach', 'Monitor pipeline']
    };
  }
  
  const topRisk = atRiskDeals.sort((a, b) => (b.value || 0) - (a.value || 0))[0];
  
  return {
    daily_brief: `${atRiskDeals.length} deal(s) at risk • €${totalRisk.toLocaleString()} exposed. ${topRisk.lead?.name || 'Top deal'} (€${(topRisk.value || 0).toLocaleString()}) hasn't moved in 7+ days. Immediate action required to prevent revenue loss.`,
    reasoning: `Identified ${atRiskDeals.length} deals with no activity in 7+ days. Total revenue at risk: €${totalRisk.toLocaleString()}.`,
    tone: 'directive',
    focus_areas: [
      `Urgent: Re-engage ${topRisk.lead?.name || 'top deal'}`,
      `Today: Contact ${atRiskDeals.slice(0, 3).map(d => d.lead?.name).filter(Boolean).join(', ')}`,
      `This week: Recover €${totalRisk.toLocaleString()} at risk`
    ]
  };
}

function generateRevenueOptimizationResponse(priorityActions: any[], deals: any[], metrics: any): ContextualResponse {
  const highValueActions = priorityActions.filter(a => (a.revenue_impact || 0) > 5000);
  const totalOpportunity = priorityActions.reduce((sum, a) => sum + (a.revenue_impact || 0), 0);
  
  if (highValueActions.length === 0) {
    return {
      daily_brief: `Your pipeline has €${metrics.pipelineValue.toLocaleString()} in opportunities. Focus on advancing deals through stages rather than new outreach. Move deals from "Qualified" to "Proposal" to accelerate revenue.`,
      tone: 'strategic',
      focus_areas: ['Advance existing deals', 'Move to proposal stage', 'Close faster']
    };
  }
  
  const topOpportunity = highValueActions[0];
  
  return {
    daily_brief: `To close €10k faster: Focus on ${topOpportunity.lead_name} (€${(topOpportunity.revenue_impact || 0).toLocaleString()}) and ${highValueActions.slice(1, 3).map(a => a.lead_name).join(', ')}. Combined opportunity: €${totalOpportunity.toLocaleString()}. These 3 actions can unlock €${Math.min(totalOpportunity, 10000).toLocaleString()} within 48 hours.`,
    reasoning: `Identified ${highValueActions.length} high-value opportunities (€5k+). Fastest path to €10k: focus on top 3.`,
    tone: 'actionable',
    focus_areas: [
      `Immediate: ${topOpportunity.recommended_action} ${topOpportunity.lead_name}`,
      `Today: Execute on top 3 high-value leads`,
      `48h goal: Unlock €${Math.min(totalOpportunity, 10000).toLocaleString()}`
    ]
  };
}

function generatePipelineHealthResponse(leads: any[], deals: any[], metrics: any): ContextualResponse {
  const healthScore = calculateHealthScore(leads, deals, metrics);
  const healthStatus = healthScore >= 75 ? 'excellent' : healthScore >= 50 ? 'good' : healthScore >= 25 ? 'needs attention' : 'critical';
  
  return {
    daily_brief: `Pipeline health: ${healthStatus.toUpperCase()} (${healthScore}/100). ${deals.length} active deals, €${metrics.pipelineValue.toLocaleString()} in pipeline, ${metrics.needAction} leads need action. ${metrics.overdueFollowups > 0 ? `⚠️ ${metrics.overdueFollowups} overdue follow-ups.` : 'All follow-ups on track.'}`,
    reasoning: `Health calculated from: deal velocity (${deals.length} active), pipeline value (€${metrics.pipelineValue.toLocaleString()}), follow-up compliance (${metrics.overdueFollowups} overdue), and stage distribution.`,
    tone: 'analytical',
    focus_areas: [
      healthScore < 50 ? 'Address overdue follow-ups' : 'Maintain follow-up cadence',
      'Advance deals through stages',
      'Fill pipeline gaps'
    ]
  };
}

function generateForecastResponse(deals: any[], metrics: any): ContextualResponse {
  const weightedForecast = metrics.forecast || (metrics.pipelineValue * 0.4);
  const winRate = metrics.winRate || 0;
  
  return {
    daily_brief: `Weighted forecast: €${weightedForecast.toLocaleString()} (${Math.round(winRate)}% win rate). Based on ${deals.length} active deals with probability-weighted values. Conservative estimate assumes current momentum continues.`,
    reasoning: `Forecast = sum of (deal value × probability). Current pipeline: €${metrics.pipelineValue.toLocaleString()}, weighted: €${weightedForecast.toLocaleString()}.`,
    tone: 'analytical',
    focus_areas: [
      'Increase deal probabilities',
      'Close high-probability deals',
      'Improve win rate'
    ]
  };
}

function generateStuckDealsResponse(deals: any[], metrics: any): ContextualResponse {
  const stuck = deals.filter(d => {
    const daysSinceUpdate = d.updated_at ? 
      Math.floor((Date.now() - new Date(d.updated_at).getTime()) / (1000 * 60 * 60 * 24)) : 999;
    return daysSinceUpdate > 7 && d.stage !== 'won' && d.stage !== 'lost';
  });
  
  if (stuck.length === 0) {
    return {
      daily_brief: `All deals are moving. No stuck deals detected. Keep the momentum going.`,
      tone: 'analytical',
      focus_areas: ['Maintain momentum', 'Continue outreach']
    };
  }
  
  const stuckValue = stuck.reduce((sum, d) => sum + (d.value || 0), 0);
  
  return {
    daily_brief: `${stuck.length} deal(s) stuck (7+ days no movement): ${stuck.slice(0, 3).map(d => d.lead?.name || 'Deal').join(', ')}. Total stuck value: €${stuckValue.toLocaleString()}. Re-engage immediately or move to lost.`,
    reasoning: `Identified ${stuck.length} deals with no activity in 7+ days. These represent €${stuckValue.toLocaleString()} in potential revenue.`,
    tone: 'directive',
    focus_areas: [
      'Re-engage stuck deals',
      'Decide: move forward or close as lost',
      `Recover €${stuckValue.toLocaleString()}`
    ]
  };
}

function generateNextStepsResponse(priorityActions: any[]): ContextualResponse {
  if (priorityActions.length === 0) {
    return {
      daily_brief: `No immediate next steps identified. Review your pipeline to identify opportunities.`,
      tone: 'actionable',
      focus_areas: ['Review pipeline', 'Identify opportunities']
    };
  }
  
  const steps = priorityActions.slice(0, 3).map((a, i) => 
    `${i + 1}. ${a.recommended_action} ${a.lead_name} (${a.why})`
  ).join('\n');
  
  return {
    daily_brief: `Next steps:\n${steps}\n\nStart with step 1. Each action takes ~10 minutes.`,
    tone: 'actionable',
    focus_areas: priorityActions.slice(0, 3).map(a => `${a.recommended_action} ${a.lead_name}`)
  };
}

function generateActionPlanResponse(priorityActions: any[], metrics: any): ContextualResponse {
  return {
    daily_brief: `Action plan for today: ${priorityActions.slice(0, 3).map(a => a.lead_name).join(', ')}. Total: ${priorityActions.length} priority actions. Estimated time: ${priorityActions.length * 10} minutes. Focus on revenue impact: €${priorityActions.reduce((sum, a) => sum + (a.revenue_impact || 0), 0).toLocaleString()}.`,
    reasoning: `Plan based on ${metrics.needAction} leads needing action. Prioritized by revenue impact and recency.`,
    tone: 'actionable',
    focus_areas: priorityActions.slice(0, 5).map(a => `${a.lead_name} (€${(a.revenue_impact || 0).toLocaleString()})`)
  };
}

function generateGeneralResponse(priorityActions: any[], metrics: any): ContextualResponse {
  const topAction = priorityActions[0];
  
  return {
    daily_brief: topAction
      ? `You have ${metrics.needAction} leads needing action. Start with ${topAction.lead_name} - ${topAction.why}. Revenue impact: €${(topAction.revenue_impact || 0).toLocaleString()}.`
      : `Your pipeline has €${metrics.pipelineValue.toLocaleString()} in opportunities. ${metrics.needAction} leads need attention.`,
    tone: 'directive',
    focus_areas: ['Review pipeline', 'Take action', 'Track outcomes']
  };
}

function calculateHealthScore(leads: any[], deals: any[], metrics: any): number {
  let score = 50; // Base score
  
  // Deal velocity (active deals)
  if (deals.length > 10) score += 15;
  else if (deals.length > 5) score += 10;
  else if (deals.length > 0) score += 5;
  
  // Pipeline value
  if (metrics.pipelineValue > 50000) score += 15;
  else if (metrics.pipelineValue > 20000) score += 10;
  else if (metrics.pipelineValue > 5000) score += 5;
  
  // Follow-up compliance
  if (metrics.overdueFollowups === 0) score += 15;
  else if (metrics.overdueFollowups < 3) score += 5;
  else score -= 10;
  
  // Win rate
  if (metrics.winRate > 50) score += 15;
  else if (metrics.winRate > 30) score += 10;
  else if (metrics.winRate > 0) score += 5;
  
  return Math.max(0, Math.min(100, score));
}

